import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  cancelLedgerImport,
  closeLedgerImport,
  commitLedgerImport,
  downloadLedgerTemplate,
  fetchLedgerImport,
  uploadLedgerSheet,
  type LedgerImportReviewDTO,
  type LedgerPushDTO,
} from "../../data/ledgerImportApi";
import { hasBeenPushed } from "../../domain/ledgerImport";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";
import { LedgerImportRows } from "./LedgerImportRows";
import { LedgerImportSummary } from "./LedgerImportSummary";

/**
 * Bulk import: download the chart of accounts, edit it, upload it back, push it.
 *
 * THE DOWNLOAD IS THE TEMPLATE. There is no blank form to fill in — the sheet
 * arrives holding what Zoho currently has, every row carrying its own Zoho id.
 * That is what makes the round trip safe: a row with an id is an edit to a
 * ledger we can name, and a row without one is unambiguously an addition.
 *
 * UPLOAD REACHES NOTHING. It parses, diffs and stores; the answer is what WOULD
 * change. Only the push writes, only from the confirm below, and nothing it does
 * can be undone.
 */

type Phase = "loading" | "idle" | "uploading" | "review";

/**
 * How many batches one press of Push is allowed to run.
 *
 * The loop exists because Zoho has no bulk-create and one HTTP request cannot
 * hold forty sequential writes. The cap exists because a loop driven by a server
 * field is a loop that hammers the server if that field ever gets stuck — the
 * page stops and offers Continue rather than spinning. Twenty-five rows a batch
 * makes this a thousand-row ceiling, which no chart of accounts reaches.
 */
const MAX_BATCHES = 40;

export function LedgerImportPanel({
  clientId,
  clientName,
  organisationName,
  onPushed,
}: {
  clientId: ClientId;
  clientName: string;
  /** Named on the push button, because "which books am I writing to" is the question. */
  organisationName?: string | null;
  /**
   * A push finished and the chart of accounts above this panel is now out of
   * date — it was read before two ledgers existed. Without this the CA pushes,
   * sees "created 2", scrolls up to admire them and finds the old list, which
   * reads as the push not having worked.
   */
  onPushed?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [review, setReview] = useState<LedgerImportReviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [push, setPush] = useState<LedgerPushDTO | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // A push spans several requests. If the step unmounts part-way — the CA
  // clicked to another sub-step — the remaining batches must not keep firing
  // into a screen nobody is watching.
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;

    fetchLedgerImport(clientId)
      .then((result) => {
        if (!liveRef.current) return;
        setReview(result);
        setPhase(result.import === null ? "idle" : "review");
      })
      .catch(() => {
        // A review we could not load is not worth an error banner: the panel
        // still works, it just starts empty.
        if (liveRef.current) setPhase("idle");
      });

    return () => {
      liveRef.current = false;
    };
  }, [clientId]);

  function download() {
    setBusy(true);
    setError(null);

    downloadLedgerTemplate(clientId)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not build the sheet.");
      })
      .finally(() => setBusy(false));
  }

  function upload(file: File) {
    setPhase("uploading");
    setError(null);
    setPush(null);
    setConfirming(false);

    uploadLedgerSheet(clientId, file)
      .then((result) => {
        setReview(result);
        setPhase("review");
      })
      .catch((cause: unknown) => {
        // Every message the server sends here is written for a CA — "that sheet
        // has a header but no ledgers under it" — so it goes on screen as it is.
        setError(cause instanceof Error ? cause.message : "Could not read that sheet.");
        setPhase(review?.import ? "review" : "idle");
      })
      .finally(() => {
        // Otherwise picking the same file twice after a fix fires no change event.
        if (inputRef.current) inputRef.current.value = "";
      });
  }

  function discard() {
    setBusy(true);

    cancelLedgerImport(clientId)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setError(null);
        setPush(null);
        setConfirming(false);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not discard the import.");
      })
      .finally(() => setBusy(false));
  }

  function close() {
    setBusy(true);

    closeLedgerImport(clientId)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setPush(null);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not close the import.");
      })
      .finally(() => setBusy(false));
  }

  /**
   * Run the push to completion, or to the first reason to stop.
   *
   * Sequential and deliberately so — each call tells us what is left, and the
   * rows a batch creates are the parents the next batch hangs children off.
   * Overlapping two would race on exactly that.
   */
  async function runPush(acknowledgeDuplicate: boolean) {
    setConfirming(false);
    setPushing(true);
    setError(null);

    try {
      // The server's queue only holds rows it can still act on, so
      // "committing" always means real work is left and every batch moves
      // forward. There is no longer a no-progress case for the browser to
      // detect: the loop that used to watch the settled count for a number
      // that stopped moving existed because refused rows were re-offered
      // forever, and they are not any more.

      for (let batch = 0; batch < MAX_BATCHES; batch++) {
        const result = await commitLedgerImport(clientId, acknowledgeDuplicate);
        if (!liveRef.current) return;

        setReview(result);
        setPush(result.push ?? null);

        // Halted is not "keep going slower" — it is a rate limit or a refused
        // credential, and the next call would hit the same wall.
        if (!result.push || result.push.status === "done" || result.push.halted) {
          // The server re-read the chart from Zoho as the last act of the push,
          // so the parent only has to pick that up. Not on a halt or a partial
          // batch: mid-push our stored copy is still the pre-push one.
          if (result.push?.status === "done") onPushed?.();
          break;
        }
      }
    } catch (cause: unknown) {
      if (!liveRef.current) return;

      setError(cause instanceof Error ? cause.message : "The push stopped.");

      // Whatever went wrong, the truth about which rows made it is on the
      // server, not in whatever we last rendered.
      await fetchLedgerImport(clientId)
        .then((fresh) => {
          if (liveRef.current) setReview(fresh);
        })
        .catch(() => undefined);
    } finally {
      if (liveRef.current) setPushing(false);
    }
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) upload(file);
  }

  if (phase === "loading") {
    return (
      <section className="flex flex-col gap-3 rounded-lg border p-4">
        <h3 className="text-base font-medium">Bulk Import Your Ledgers</h3>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  const summary = review?.import ?? null;
  const rows = review?.rows ?? [];
  const pushed = summary !== null && hasBeenPushed(summary);
  const toPush = summary === null ? 0 : summary.new_rows + summary.changed_rows;
  // Same phrasing as the No Bill panel's push button, and for the same reason:
  // the organisation name on its own read as an environment rather than as a
  // customer's accounting system.
  const books = `${organisationName ?? clientName}'s Zoho Books`;
  // Rows Zoho refused are worth another go — usually after fixing the thing it
  // complained about in Zoho itself. Rows it never got to are not: they are
  // waiting on a parent that will never exist.
  const retryable = summary !== null && summary.status === "committing";

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">Bulk Import Your Ledgers</h3>
          <p className="text-sm text-muted-foreground">
            Use the template below to add multiple ledgers at once. Download the sheet, enter the ledgers you want
            to add, and upload the completed sheet here.
          </p>
        </div>
        <CoButton
          variant="outline"
          onClick={download}
          disabled={busy || pushing || phase === "uploading"}
        >
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {summary === null ? (
        <>
          {/*
            A label wrapping a hidden input rather than a button: it gives
            keyboard and screen-reader behaviour for free, which a div with an
            onClick does not.

            `relative` is load-bearing, not styling. The input below is `sr-only`,
            which is `position: absolute`, and without a positioned ancestor it
            resolves against the BODY — escaping the step's scroll container and
            landing at whatever document offset the panel happens to sit at. It
            then becomes the tallest thing on the page, stretching a layout that
            is meant to be exactly one screen tall to nearly two.

            The symptom is not a misplaced input, because nobody can see it. It
            is that cancelling the file picker returns focus to the input, the
            browser scrolls the focused element into view, and the window lands
            past the bottom of the app on empty document. The whole page goes
            white with no error anywhere, which is a long way to travel from a
            missing `position: relative`.
          */}
          <label
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn(
              "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
              dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
              phase === "uploading" && "pointer-events-none opacity-70"
            )}
          >
            <UploadIcon className="size-5 text-muted-foreground" />
            <span className="text-sm font-medium">
              {phase === "uploading" ? "Reading the sheet…" : "Drop the filled-in sheet here, or click to choose"}
            </span>
            <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 2 MB</span>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              className="sr-only"
              disabled={phase === "uploading"}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload(file);
              }}
            />
          </label>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{summary.original_filename}</span>
            <span className="text-xs text-muted-foreground">{summary.total_rows} rows read</span>
          </div>

          <LedgerImportSummary summary={summary} rows={rows} push={push} pushing={pushing} />
          <LedgerImportRows rows={rows} showOutcomes={pushed} />

          <div className="flex flex-col gap-3 border-t pt-4">
            {confirming ? (
              <PushConfirm
                count={toPush}
                books={books}
                alreadyPushedAt={summary.already_pushed_at}
                onConfirm={() => void runPush(true)}
                onCancel={() => setConfirming(false)}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                {summary.status === "done" ? (
                  // Clears the finished import and brings the dropzone back, so
                  // the label says where it leads rather than "Close". No caption
                  // about rows not added: LedgerImportRejected already says what
                  // to do with each one (ZOUR-257).
                  <CoButton onClick={close} disabled={busy}>
                    <UploadIcon className="size-4" />
                    Upload more
                  </CoButton>
                ) : (
                  <>
                    {/*
                      One click, straight to Zoho — the review above IS the
                      confirmation. The CA uploaded this sheet, read a diff, and
                      is looking at the two ledgers by name and row number. A
                      dialog restating that adds nothing, and a confirm that
                      never says anything new is one people learn to click
                      through, so it stops working on the day it matters.

                      The button names the organisation instead, because that is
                      the one fact the review never shows and the one mistake
                      worth catching: a seat can be reset and re-pointed, and
                      "whose books" is not answerable from a list of ledgers.
                    */}
                    <CoButton
                      onClick={() =>
                        summary.already_pushed_at !== null ? setConfirming(true) : void runPush(false)
                      }
                      disabled={pushing || busy || toPush === 0}
                    >
                      {pushing
                        ? "Pushing…"
                        : retryable
                          ? `Continue - ${push?.remaining ?? "some"} left`
                          : `Push ${toPush} to ${books}`}
                    </CoButton>
                    <CoButton variant="outline" onClick={discard} disabled={busy || pushing}>
                      Discard
                    </CoButton>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The gate for pushing a file that has already been pushed.
 *
 * NOT a generic "are you sure" — an ordinary push does not come through here at
 * all. This exists for the one case where the screen knows something the CA
 * demonstrably does not, because if they knew it they would not be pressing the
 * button: this exact spreadsheet has been through already, and Zoho has no
 * idempotency key, so sending it again means a second copy of every new ledger
 * on it. Not blocked, because re-running after a partial failure is a real
 * thing people need to do.
 *
 * Inline rather than a modal: the rows it is talking about are just above, and a
 * dialog would cover them at exactly the moment somebody wants to look again.
 */
function PushConfirm({
  count,
  books,
  alreadyPushedAt,
  onConfirm,
  onCancel,
}: {
  count: number;
  books: string;
  alreadyPushedAt: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
      <p className="text-sm">
        This exact file was already pushed on{" "}
        <span className="font-medium">{alreadyPushedAt}</span>.
      </p>
      <p className="text-sm text-muted-foreground">
        Zoho cannot recognise a repeat, so sending it again adds every new ledger on it a second
        time to <span className="font-medium">{books}</span> - and a ledger with entries posted to
        it cannot be deleted. Continue only if you are re-running a push that did not finish.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <CoButton onClick={onConfirm}>
          Push {count} again anyway
        </CoButton>
        <CoButton variant="outline" onClick={onCancel}>
          Cancel
        </CoButton>
      </div>
    </div>
  );
}
